import React, { useEffect, useRef, useCallback, useState, useMemo, useContext } from 'react';
import * as d3 from 'd3';
import { v4 as uuidv4 } from 'uuid';

import MobileContext from 'Shared/contexts/MobileContext';

import {
  drawItems,
  createMasks,
  createBlurs,
  drawBluredItems,
  drawMasks,
  addMasks,
  clearBlur,
  drawClearedItems,
} from './tools';
import { getDistance, getIdSelector } from './utils';

import './index.scss';

type Props = {
  erased: boolean;
  setErased: Function;
  circleRadius: number;
  erasable: boolean;
  hint?: string;
  cleared: boolean;
  onClear?: Function;
  attributes?: {
    width?: number;
    height?: number;
    blurAmount?: number;
    items?: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      text: string;
      url: string;
      style: string;
      className: string;
    }>;
  };
};

function getCoef(items) {
  if (items.length >= 3) return 2;
  if (items.length === 1 && items[0].tagName === 'P') return 0.5;
  return 3;
}

function D3Component({
  erasable = true,
  hint,
  cleared = false,
  circleRadius,
  onClear,
  attributes,
  erased,
  setErased,
}: Props) {
  const [touched, setTouched] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const isMobile = useContext(MobileContext);

  const prevCoords = useRef({ x: 0, y: 0 });
  const distance = useRef(0);
  const itemKey = useMemo(() => uuidv4(), []);

  const maskId = `mask${itemKey}`;
  const blueMaskId = `blueMask${itemKey}`;

  const imageBlurId = `blur${itemKey}`;
  const textBlurId = `textBlur${itemKey}`;

  const itemId = `container${itemKey}`;
  const svgId = `svg${itemKey}`;

  const blurs = [
    { id: imageBlurId, amount: attributes?.blurAmount },
    { id: textBlurId, amount: attributes?.blurAmount / 3 },
  ];

  const draw = useCallback(() => {
    const svg = d3.select(getIdSelector(svgId));
    const defs = svg.append('defs');

    if (cleared) {
      drawClearedItems(svg, attributes);
      return;
    }

    const masks = createMasks(defs, [maskId, blueMaskId]);
    createBlurs(defs, blurs);

    drawBluredItems(svg, attributes, { imageBlur: blurs[0], textBlur: blurs[1] });
    drawItems(svg, attributes, { maskId, blueMaskId });

    drawMasks(attributes, { blueMask: masks[1], mask: masks[0] });
  }, [attributes, blueMaskId, blurs, cleared, maskId, svgId]);

  useEffect(() => {
    draw();
  }, []);

  const mouseMove = useCallback(() => {
    if (!isDrawing) return;
    setTouched(true);

    const container = document.getElementById(itemId);
    const [x, y] = d3.mouse(container);

    const mask = d3.select(container).select(getIdSelector(maskId));
    const blueMask = d3.select(container).select(getIdSelector(blueMaskId));

    addMasks({ x, y, circleRadius, mask, blueMask });
    distance.current = getDistance(distance.current, x, y, prevCoords.current.x, prevCoords.current.y);

    prevCoords.current = { x, y };
  }, [attributes.height, blueMaskId, circleRadius, isDrawing, itemId, maskId]);

  const mouseDown = useCallback(() => {
    setIsDrawing(true);
  }, []);

  const mouseSvgDown = useCallback(() => {
    setTouched(true);
    setIsDrawing(true);
  }, []);

  const mouseUp = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleMouseEnter = useCallback(event => {
    if (event.buttons === 1) setTouched(true);
  }, []);

  useEffect(() => {
    if (!erased && erasable && !cleared) {
      const svg = d3.select(getIdSelector(svgId));
      svg.on('mousemove', mouseSvgDown);
      svg.on('mousemove', mouseMove);
      svg.on('touchmove', mouseSvgDown);
      svg.on('touchmove', mouseMove);
      document.addEventListener('mousedown', mouseDown);
      document.addEventListener('mouseup', mouseUp);
      document.addEventListener('touchstart', mouseDown);
      document.addEventListener('touchend', mouseUp);
    }
    return () => {
      if (!erased && erasable) {
        document.removeEventListener('mousedown', mouseDown);
        document.removeEventListener('mouseup', mouseUp);
        document.removeEventListener('touchstart', mouseDown);
        document.removeEventListener('touchend', mouseUp);
      }
    };
  }, [erased, erasable, mouseDown, mouseMove, mouseUp, cleared, itemId, svgId, mouseSvgDown]);

  useEffect(() => {
    if (!cleared && erased) {
      if (onClear) setTimeout(onClear, 2000);
      const container = d3.select(getIdSelector(itemId));

      clearBlur(container, attributes, { maskId, blueMaskId });
    }
  }, [attributes, blueMaskId, cleared, erased, itemId, maskId, onClear]);

  useEffect(() => {
    const { width, height, items } = attributes;
    const coef = getCoef(items);
    if (!cleared && distance.current >= (height + width) * coef) {
      setErased(true);
    }
  }, [onClear, cleared, attributes, distance.current]);

  return (
    <div id={itemId} className="erasable-blurred-component-wrapper">
      <svg
        id={svgId}
        style={{ width: isMobile ? '100%' : null }}
        height={attributes.height}
        width={attributes.width}
        className={erased ? '' : `erasable-blurred-component`}
      />
      {!cleared ? (
        <>
          {erasable ? (
            <div
              className={`erasable-blurred-component-hint${touched ? '--hide' : ''}`}
              onMouseEnter={handleMouseEnter}
              onMouseDown={mouseSvgDown}
            >
              <p>Move your mouse to scratch off and view your kudos</p>
            </div>
          ) : (
            <div className="erasable-blurred-component-hint">
              <p>{hint}</p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default D3Component;
